import type { AppUser } from '~/models'
import { PullService } from './servicePull'
import type { Query, QueryDocumentSnapshot } from 'firebase/firestore'
import { documentId, getDocs, limit, query, where } from 'firebase/firestore'

export class AppUserServicePull extends PullService<AppUser, AppUser[]> {
  private readonly collection
  private readonly collectionGroup

  constructor() {
    super()
    const { collection, collectionGroup } = useFirebase()
    this.collection = collection('AppUsers')
    this.collectionGroup = collectionGroup('ExternalUserData')
  }

  @PullService.CallableFunction<AppUser[]>()
  public async listenForSearchQuery(searchQuery: string): Promise<AppUser[]> {
    const isNumeric = (str: string) => /^\d+$/.test(str)

    if (isNumeric(searchQuery) && searchQuery.length <= 8) {
      const appUserIds = await this.getAppUserIdsByAproId(searchQuery)
      return this.getByAppUserIds(appUserIds)
    }
    const appUserPromise = this.getByAppUserIds([searchQuery])
    const emailPromise = this.getByEmail(searchQuery)

    return [...await appUserPromise, ...await emailPromise]
  }

  private async getAppUserIdsByAproId(aproId: string): Promise<string[]> {
    const q = this.buildStartsWithQuery(this.collectionGroup, aproId)

    const snapshot = await getDocs(q)

    return snapshot.docs.map((doc) => {
      return doc.ref.parent.parent?.id ?? ''
    })
  }

  private async getByAppUserIds(appUserIds: string[]): Promise<AppUser[]> {
    const q = query(this.collection, where(documentId(), 'in', appUserIds))

    const snapshot = await getDocs(q)

    return snapshot.docs.map(doc => this.toAppUser(doc))
  }

  private async getByEmail(searchQuery: string): Promise<AppUser[]> {
    const q = query(
      this.collection,
      where('Email', '>=', searchQuery),
      where('Email', '<=', `${searchQuery}\uF8FF`),
      limit(50),
    )

    const snapshot = await getDocs(q)

    return snapshot.docs.map(doc => this.toAppUser(doc))
  }

  /** AppUsers documents hold no id field, so it has to come off the snapshot. */
  private toAppUser(doc: QueryDocumentSnapshot): AppUser {
    return { ...doc.data(), id: doc.id, ref: doc.ref } as AppUser
  }

  private buildStartsWithQuery(collection: any, searchQuery: string): Query {
    const startCode = searchQuery
    const endCode = this.getEndCode(startCode)

    return query(
      collection,
      where('PatientId', '>=', startCode),
      where('PatientId', '<', endCode),
      limit(50),
    )
  }

  private getEndCode(startCode: string): string {
    const strlength = startCode.length
    const strFrontCode = startCode.slice(0, strlength - 1)
    const strEndCode = startCode.slice(strlength - 1, startCode.length)

    return strFrontCode + String.fromCharCode(strEndCode.charCodeAt(0) + 1)
  }
}
